/**
 * Time helpers for the queue board.
 *
 * Deliberately free of imports. `utils.js` reaches `constants.js`, which
 * reaches the shared palette and so `@cuemath/comps` — ESM that
 * react-scripts' jest cannot parse — and that made every pure function in
 * this screen untestable. These are the ones worth pinning, so they live
 * where the test runner can get at them.
 */

const pad = n => String(n).padStart(2, '0');

// Every timestamp in this service is naive IST — the pods run on IST local
// time and `created_on` is written with datetime.now(). So the browser's own
// local rendering is correct only for a viewer already in IST; we format
// explicitly rather than rely on that.
export const IST_OFFSET_MINUTES = 330;

const toIst = date =>
  new Date(date.getTime() + (IST_OFFSET_MINUTES + date.getTimezoneOffset()) * 60000);

export const fmtDateTimeLocal = date => {
  const d = toIst(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

export const fmtClock = date => {
  const d = toIst(date);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Epoch seconds for a `datetime-local` value the user typed, read as IST.
 *
 * Returns null for anything unparseable, including an implausible year.
 * Chrome fires `change` per digit typed into the year segment, so editing
 * a year to 2026 emits 0002, 0020, 0202, 2026 — and `Date.UTC` maps 0-99
 * onto 1900-1999, so those became 1902, 1920 and 0202. Each was a valid
 * float to both services, passed the gateway's bounds check, and reached
 * chatbackend as an effectively unbounded window that scanned the whole
 * ticket table — three times per year edit, since nothing debounces.
 */
// No support ticket predates this, so anything below it is a half-typed
// year rather than a window anyone means.
const MIN_YEAR = 2000;

export const istInputToEpoch = raw => {
  if (!raw) return null;
  const [datePart, timePart] = raw.split('T');
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (![y, m, d, hh, mm].every(Number.isFinite) || y < MIN_YEAR) return null;
  const epoch = Date.UTC(y, m - 1, d, hh, mm) / 1000 - IST_OFFSET_MINUTES * 60;
  return Number.isFinite(epoch) ? epoch : null;
};

export const startOfIstToday = () => `${fmtDateTimeLocal(new Date()).slice(0, 10)}T00:00`;

/** Compact elapsed — `4m`, `2h 13m`, `19h 41m`. */
export const fmtElapsed = seconds => {
  if (seconds === null || seconds === undefined) return '';
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 24) return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
};

export const elapsedSince = (epochSeconds, nowSeconds) => {
  if (!epochSeconds) return null;
  return Math.max(0, nowSeconds - epochSeconds);
};

export const shortId = id => (id ? String(id).slice(0, 8) : '');
